import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { http, errMsg } from "../../lib/api";

const PRICE = /((?:(?:A|AU|NZ|US)?\$|AUD\s?|€|£)\s?\d[\d,]*(?:\.\d{1,2})?(?:\s?(?:–|-|to)\s?\$?\d[\d,]*(?:\.\d{1,2})?)?|\b\d[\d,]*\s?dollars\b|\b\d{1,2}%)/gi;

const Marked = ({ t }) => t.split(PRICE).map((p, i) => (i % 2 ? <mark key={i} className="bg-red-100 text-red-700 line-through">{p}</mark> : p));

const pairs = (c) => {
  const b = c.before, a = c.after, out = [];
  b.paragraphs.forEach((p, i) => { if (p !== a.paragraphs[i]) out.push([p, a.paragraphs[i] || ""]); });
  (b.detail || []).forEach((d, i) => { const n = a.detail?.[i]?.body; if (d.body !== n) out.push([`${d.title}: ${d.body}`, `${d.title}: ${n || ""}`]); });
  return out;
};

const Change = ({ c, i }) => (
  <div data-testid={`price-strip-change-${i}`} className="border-t border-stone-100 py-3">
    <div className="text-sm font-bold text-[#2B2A26]">{c.name} <span className="font-normal text-xs text-stone-500">· {c.section}{c.after.method === "simple" ? " · simple cut (AI unavailable)" : ""}</span></div>
    {pairs(c).map(([b, a], j) => (
      <div key={j} className="mt-2 grid gap-2 text-xs leading-relaxed md:grid-cols-2">
        <p className="rounded bg-stone-50 p-2 text-stone-600"><Marked t={b} /></p>
        <p className="rounded bg-emerald-50/60 p-2 text-stone-800">{a}</p>
      </div>
    ))}
    {c.after.costs_add?.length > 0 && (
      <div className="mt-2 text-xs text-[#9A7B2C]">Added to What it costs: {c.after.costs_add.map((x) => `${x.item} ${x.amount}`).join(" · ")}</div>
    )}
  </div>
);

export const PriceStripPreview = ({ kitId, onClose, onApplied }) => {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (kitId) http.get(`/kits/${kitId}/price-strip`).then((x) => setD(x.data)).catch((e) => toast.error(errMsg(e))); }, [kitId]);
  const apply = async () => {
    setBusy(true);
    try { const r = await http.post(`/kits/${kitId}/price-strip/apply`); toast.success(`Prices moved out of ${r.data.venues} venue description(s)`); onApplied(); onClose(); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <Dialog open={!!kitId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="price-strip-preview" className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{d ? `${d.destination} — ${d.changes.length} venue(s)` : "Loading…"}</DialogTitle></DialogHeader>
        {!d ? <Loader2 className="animate-spin text-[#9A7B2C]" /> : (
          <>
            <p className="text-xs text-stone-500">Left: now · Right: after. Prices crossed out in red are removed from the description and stay under What it costs.</p>
            {d.changes.map((c, i) => <Change key={c.block} c={c} i={i} />)}
            {d.status === "ready" && (
              <button data-testid="price-strip-apply-btn" disabled={busy} onClick={apply} className="mt-3 inline-flex items-center gap-1.5 rounded-sm bg-[#C9A847] px-4 py-2 text-sm font-bold text-white hover:bg-[#B8973A] disabled:opacity-50">
                {busy && <Loader2 size={13} className="animate-spin" />} Apply to this kit
              </button>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
